import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import styles from "./AuditSettings.module.scss";
import TitleUpdater from "../../../components/common/title/TitleUpdater";
import AuditService from "../../../api/services/audit.service";

interface AuditSettings {
  tax_percent: number;
  expenses_percent: number;
  // Delivery is configured in USD and converted into the buyer's currency.
  delivery_fee_usd: number;
  free_delivery_threshold_usd: number;
}

const AuditSettingsPage = () => {
  const { t } = useTranslation("translation");
  const [settings, setSettings] = useState<AuditSettings>({
    tax_percent: 0,
    expenses_percent: 0,
    delivery_fee_usd: 0,
    free_delivery_threshold_usd: 0,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError("");

      console.log("Fetching audit settings...");
      const response = await AuditService.getSettings();
      console.log("Audit settings response:", response);

      setSettings({
        tax_percent: response.data?.tax_percent ?? 0,
        expenses_percent: response.data?.expenses_percent ?? 0,
        delivery_fee_usd: response.data?.delivery_fee_usd ?? 0,
        free_delivery_threshold_usd:
          response.data?.free_delivery_threshold_usd ?? 0,
      });
    } catch (err: any) {
      console.error("Fetch settings error:", err);
      console.error("Error response:", err?.response);
      console.error("Error response data:", err?.response?.data);
      console.error("Error response status:", err?.response?.status);
      console.error("Error config:", err?.config);

      const errorMessage =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        (t("audit_settings.errors.failed_to_load") as string);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof AuditSettings, value: string) => {
    // Clear previous errors
    setError("");

    // Allow empty string
    if (value === "") {
      setSettings((prev) => ({
        ...prev,
        [field]: 0,
      }));
      return;
    }

    const numValue = parseFloat(value);

    // Validate: must be between 0 and 100
    if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
      setSettings((prev) => ({
        ...prev,
        [field]: numValue,
      }));
    } else {
      setError(t("audit_settings.errors.percentage_range") as string);
    }
  };

  const handleAmountChange = (field: keyof AuditSettings, value: string) => {
    setError("");

    if (value === "") {
      setSettings((prev) => ({ ...prev, [field]: 0 }));
      return;
    }

    const numValue = parseFloat(value);

    if (!isNaN(numValue) && numValue >= 0) {
      setSettings((prev) => ({ ...prev, [field]: numValue }));
    } else {
      setError(t("audit_settings.errors.amount_negative") as string);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (settings.tax_percent < 0 || settings.tax_percent > 100) {
      setError(t("audit_settings.errors.tax_percentage_range") as string);
      return;
    }
    if (settings.expenses_percent < 0 || settings.expenses_percent > 100) {
      setError(t("audit_settings.errors.expenses_percentage_range") as string);
      return;
    }
    if (
      settings.delivery_fee_usd < 0 ||
      settings.free_delivery_threshold_usd < 0
    ) {
      setError(t("audit_settings.errors.amount_negative") as string);
      return;
    }

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      await AuditService.updateSettings({
        tax_percent: settings.tax_percent,
        expenses_percent: settings.expenses_percent,
        delivery_fee_usd: settings.delivery_fee_usd,
        free_delivery_threshold_usd: settings.free_delivery_threshold_usd,
      });

      setSuccess(t("audit_settings.messages.updated_successfully") as string);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(""), 3000);
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        (t("audit_settings.errors.failed_to_update") as string);
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    fetchSettings();
    setError("");
    setSuccess("");
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <TitleUpdater title={t("audit_settings.title") as string} />
        <div className={styles.loading}>
          {t("audit_settings.loading") as string}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <TitleUpdater title={t("audit_settings.title") as string} />
      <div className={styles.header}>
        <h1 className={styles.title}>{t("audit_settings.title") as string}</h1>
        <p className={styles.subtitle}>
          {t("audit_settings.subtitle") as string}
        </p>
      </div>

      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.cardsContainer}>
          {/* Tax Percentage Card */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h2 className={styles.cardTitle}>
                {t("audit_settings.tax_percentage.title") as string}
              </h2>
              <span className={styles.badge}>
                {t("audit_settings.tax_percentage.badge") as string}
              </span>
            </div>
            <div className={styles.cardBody}>
              <div className={styles.inputGroup}>
                <label htmlFor="tax_percent" className={styles.label}>
                  {t("audit_settings.tax_percentage.rate_label") as string}
                </label>
                <div className={styles.inputWrapper}>
                  <input
                    type="number"
                    id="tax_percent"
                    className={styles.input}
                    value={settings.tax_percent}
                    onChange={(e) =>
                      handleInputChange("tax_percent", e.target.value)
                    }
                    step="0.01"
                    min="0"
                    max="100"
                    placeholder="0.00"
                    disabled={saving}
                  />
                  <span className={styles.inputAddon}>%</span>
                </div>
                <p className={styles.description}>
                  {t("audit_settings.tax_percentage.description") as string}
                </p>
              </div>
              <div className={styles.preview}>
                <span className={styles.previewLabel}>Current Rate:</span>
                <span className={styles.previewValue}>
                  {settings.tax_percent.toFixed(2)}%
                </span>
              </div>
            </div>
          </div>

          {/* Expenses Percentage Card */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h2 className={styles.cardTitle}>
                {t("audit_settings.expenses_percentage.title") as string}
              </h2>
              <span className={styles.badge}>
                {t("audit_settings.expenses_percentage.badge") as string}
              </span>
            </div>
            <div className={styles.cardBody}>
              <div className={styles.inputGroup}>
                <label htmlFor="expenses_percent" className={styles.label}>
                  {t("audit_settings.expenses_percentage.rate_label") as string}
                </label>
                <div className={styles.inputWrapper}>
                  <input
                    type="number"
                    id="expenses_percent"
                    className={styles.input}
                    value={settings.expenses_percent}
                    onChange={(e) =>
                      handleInputChange("expenses_percent", e.target.value)
                    }
                    step="0.01"
                    min="0"
                    max="100"
                    placeholder="0.00"
                    disabled={saving}
                  />
                  <span className={styles.inputAddon}>%</span>
                </div>
                <p className={styles.description}>
                  {
                    t(
                      "audit_settings.expenses_percentage.description",
                    ) as string
                  }
                </p>
              </div>
              <div className={styles.preview}>
                <span className={styles.previewLabel}>Current Rate:</span>
                <span className={styles.previewValue}>
                  {settings.expenses_percent.toFixed(2)}%
                </span>
              </div>
            </div>
          </div>

          {/* Delivery Card */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h2 className={styles.cardTitle}>
                {t("audit_settings.delivery.title") as string}
              </h2>
              <span className={styles.badge}>
                {t("audit_settings.delivery.badge") as string}
              </span>
            </div>
            <div className={styles.cardBody}>
              <div className={styles.inputGroup}>
                <label htmlFor="delivery_fee_usd" className={styles.label}>
                  {t("audit_settings.delivery.fee_label") as string}
                </label>
                <div className={styles.inputWrapper}>
                  <input
                    type="number"
                    id="delivery_fee_usd"
                    className={styles.input}
                    value={settings.delivery_fee_usd}
                    onChange={(e) =>
                      handleAmountChange("delivery_fee_usd", e.target.value)
                    }
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    disabled={saving}
                  />
                  <span className={styles.inputAddon}>USD</span>
                </div>
                <p className={styles.description}>
                  {t("audit_settings.delivery.fee_description") as string}
                </p>
              </div>

              <div className={styles.inputGroup}>
                <label
                  htmlFor="free_delivery_threshold_usd"
                  className={styles.label}
                >
                  {t("audit_settings.delivery.threshold_label") as string}
                </label>
                <div className={styles.inputWrapper}>
                  <input
                    type="number"
                    id="free_delivery_threshold_usd"
                    className={styles.input}
                    value={settings.free_delivery_threshold_usd}
                    onChange={(e) =>
                      handleAmountChange(
                        "free_delivery_threshold_usd",
                        e.target.value,
                      )
                    }
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    disabled={saving}
                  />
                  <span className={styles.inputAddon}>USD</span>
                </div>
                <p className={styles.description}>
                  {t("audit_settings.delivery.threshold_description") as string}
                </p>
              </div>

              <div className={styles.preview}>
                <span className={styles.previewLabel}>
                  {t("audit_settings.delivery.preview_label") as string}
                </span>
                <span className={styles.previewValue}>
                  {settings.delivery_fee_usd <= 0
                    ? (t(
                        "audit_settings.delivery.preview_always_free",
                      ) as string)
                    : settings.free_delivery_threshold_usd > 0
                      ? `${settings.delivery_fee_usd.toFixed(2)} USD, ${t("audit_settings.delivery.preview_free_from")} ${settings.free_delivery_threshold_usd.toFixed(2)} USD`
                      : `${settings.delivery_fee_usd.toFixed(2)} USD`}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className={styles.alert} data-type="error">
            <svg
              className={styles.alertIcon}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            {error}
          </div>
        )}

        {success && (
          <div className={styles.alert} data-type="success">
            <svg
              className={styles.alertIcon}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            {success}
          </div>
        )}

        {/* Action Buttons */}
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.buttonSecondary}
            onClick={handleReset}
            disabled={saving}
          >
            {t("common.reset") as string}
          </button>
          <button
            type="submit"
            className={styles.buttonPrimary}
            disabled={saving}
          >
            {saving
              ? (t("common.saving") as string)
              : (t("audit_settings.save_button") as string)}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AuditSettingsPage;
